/**
 * Página de gestión de etiquetas nutricionales
 * Permite generar y gestionar etiquetas nutricionales según estándares MINSAL Chile
 */
import React, { useState, useEffect, useCallback } from "react";
import {
  Table,
  Card,
  Space,
  Button,
  Typography,
  Tag,
  Input,
  message,
} from "antd";
import {
  ReloadOutlined,
  CalculatorOutlined,
  DownloadOutlined,
  EyeOutlined,
} from "@ant-design/icons";
import { useBranch } from "../context/BranchContext";
import api from "../api/endpoints";
import NutritionLabelModal from "../components/nutritional/NutritionLabelModal";

const { Title, Paragraph } = Typography;
const { Search } = Input;

const NutritionLabels = () => {
  const { activeBranch } = useBranch();
  const [recipes, setRecipes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [selectedRecipe, setSelectedRecipe] = useState(null);
  const [labelModalVisible, setLabelModalVisible] = useState(false);

  const loadRecipes = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.recipes.list(1, {
        recipe_type: "NUTRITIONAL_RECIPE",
        search: search,
        branch_id: activeBranch,
      });
      const recipesList = Array.isArray(response?.results) ? response.results : [];
      setRecipes(recipesList);
    } catch (error) {
      console.error("Error loading recipes:", error);
      message.error("Error al cargar recetas: " + (error.message || "Error desconocido"));
      setRecipes([]);
    } finally {
      setLoading(false);
    }
  }, [search, activeBranch]);

  useEffect(() => {
    loadRecipes();
  }, [loadRecipes]);

  const handleViewLabel = (recipe) => {
    setSelectedRecipe(recipe);
    setLabelModalVisible(true);
  };

  const handleCalculateNutrition = async (recipe) => {
    try {
      setLoading(true);
      await api.recipes.calculateNutrition(recipe.id);
      message.success("Nutrición calculada exitosamente");
      loadRecipes();
    } catch (error) {
      message.error("Error al calcular nutrición: " + (error.message || "Error desconocido"));
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPDF = async (recipe) => {
    try {
      setLoading(true);
      const blob = await api.recipes.downloadNutritionLabelPDF(recipe.id);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `etiqueta-nutricional-${recipe.code || recipe.id}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      message.success("PDF descargado exitosamente");
    } catch (error) {
      message.error("Error al descargar PDF: " + (error.message || "Error desconocido"));
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    {
      title: "Código",
      dataIndex: "code",
      key: "code",
      width: 150,
      render: (text) => (
        <Tag color="blue" size="small">
          {text || "-"}
        </Tag>
      ),
    },
    {
      title: "Nombre",
      dataIndex: "name",
      key: "name",
      ellipsis: true,
      render: (text) => <Typography.Text strong>{text}</Typography.Text>,
    },
    {
      title: "Estado Nutricional",
      key: "nutrition_status",
      width: 180,
      render: (_, record) => (
        <Tag
          color={record.has_nutritional_calculation ? "green" : "orange"}
          size="small"
        >
          {record.has_nutritional_calculation ? "✓ Calculada" : "⚠ Sin calcular"}
        </Tag>
      ),
    },
    {
      title: "Rendimiento",
      key: "yield",
      width: 150,
      render: (_, record) => (
        <Tag color="cyan" size="small">
          {record.total_yield_grams ? `${record.total_yield_grams}g` : "-"}
        </Tag>
      ),
    },
    {
      title: "Acciones",
      key: "actions",
      width: 300,
      render: (_, record) => (
        <Space size="small">
          {!record.has_nutritional_calculation && (
            <Button
              type="link"
              icon={<CalculatorOutlined />}
              onClick={() => handleCalculateNutrition(record)}
              disabled={loading}
            >
              Calcular
            </Button>
          )}
          <Button
            type="link"
            icon={<EyeOutlined />}
            onClick={() => handleViewLabel(record)}
            disabled={!record.has_nutritional_calculation}
          >
            Ver
          </Button>
          <Button
            type="primary"
            icon={<DownloadOutlined />}
            onClick={() => handleDownloadPDF(record)}
            disabled={!record.has_nutritional_calculation}
            size="small"
          >
            PDF
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Card>
        <Space direction="vertical" style={{ width: "100%" }} size="large">
          <div>
            <Title level={2} style={{ marginBottom: 8 }}>
              Etiquetas Nutricionales para tus Productos
            </Title>
            <Paragraph type="secondary" style={{ marginBottom: 24 }}>
              Aquí puedes ver y descargar las etiquetas nutricionales de tus recetas. Las etiquetas cumplen con 
              los requisitos del Ministerio de Salud de Chile y están listas para imprimir y pegar en tus productos.
            </Paragraph>
          </div>

          <Space>
            <Search
              placeholder="Buscar recetas..."
              allowClear
              onSearch={(value) => setSearch(value)}
              style={{ width: 300 }}
            />
            <Button icon={<ReloadOutlined />} onClick={loadRecipes}>
              Actualizar
            </Button>
          </Space>

          <Table
            columns={columns}
            dataSource={Array.isArray(recipes) ? recipes : []}
            loading={loading}
            rowKey="id"
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
              showTotal: (total) => `Total: ${total} recetas con etiquetado`,
            }}
            scroll={{ x: 1000 }}
          />
        </Space>
      </Card>

      <NutritionLabelModal
        visible={labelModalVisible}
        recipe={selectedRecipe}
        onCancel={() => {
          setLabelModalVisible(false);
          setSelectedRecipe(null);
        }}
        onCalculateNutrition={() => {
          loadRecipes();
        }}
      />
    </div>
  );
};

export default NutritionLabels;

